import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { EditableComponent } from '@adobe/aem-react-editable-components'
import { ModelManager } from '@adobe/aem-spa-page-model-manager'

import {
  FilterIDs,
  type FilterProps,
  MiniCart,
  ProductGridList as PGL,
  PopoverHorizontalPositionProps,
  PopoverVerticalPositionProps,
  ProductCardChangeEvents,
  RatingAndReviewsTypes,
  StockStatuses
} from '@dcxwam/dcx-wam-ui-foundations'

import { useCookies } from 'react-cookie'

import { useLocalStorageCart } from '../../../services/product/use-cart-local-storage'
import StockService, { StockLevelStatus } from '../../../services/stock'
import { UserTypes, useUserData } from '../../../services/user/user-data'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import {
  CORESITE_LOCALSTORAGE,
  GIGYA_COOKIE_JWT,
  GIGYA_COOKIE_UID,
  HYBRIS_ACCESS_TOKEN
} from '../../../utils/constants'
import { getOptimizedImageExtension } from '../../../utils/image-extensions'
import { canUseDom, useIsSsr } from '../../../utils/isSsr'
import {
  removeParams,
  setMultipleParams,
  setParams,
  useParams,
  useRouter
} from '../../../utils/navigation'
import { checkIsPreorder } from '../../../utils/preorder'
import { getOrderQuantityValues } from '../../../utils/quantity-selector'
import { PageConfig } from '../../../utils/page-config'
import type { EditableComponentPropsType } from '../../../utils/prop-types'
import { AemConfigProxy } from '../../functional/AemConfigProxy/AemConfigProxy'

// unify !?
import CartErrorMessageDialog from '../common/cart-management/CartErrorMessageDialog'
import { handleAddToCart } from '../common/cart-management/handleAddToCart'
import {
  createDataLayerEcommerceProduct,
  type DataLayerEcommerceProduct
} from '../common/datalayer/createDataLayerEcommerceProduct'
import {
  pushDataLayerMiniCartSeeCartClick,
  pushDataLayerMiniCartValidateOrderClick,
  pushDataLayerProductListVariantChange,
  pushDataLayerProductListVariantClick,
  pushDataLayerProductListView,
  pushDataLayerSelectedFilter
} from '../common/datalayer/methods'
import {
  COLOR_PROPERTY,
  filterHistoryByStash,
  getAttributeSelectors,
  getColorSelector,
  getEnabledFilters,
  getFavourProfiles,
  getHistoryFromStash,
  getPriceInfo,
  getStockLevelStatus,
  type HistoryFilter,
  ProductListObserver,
  scrollToElementWithOffset,
  showProduct,
  SORT_PARAM_KEY,
  sortItemsByPrice,
  sortVariantsByStock,
  type Stocks,
  STOCKS_CACHE_MAX_AGE,
  stockStatusMap
} from './utils'

import type {
  GridListFilterConfig,
  GroupedItem,
  Item,
  ProductListConfig,
  Tag,
  TagGroup
} from './models'
import { fetchProductsData } from './utils/products-service'

type FilterStashType = { [filterId: TagGroup['tagGroupId']]: Tag['tagId'][] }

type ModelType = ProductListConfig & {
  initialData: {
    items: GroupedItem[]
    facets: TagGroup[]
    gcodes: string[]
  }
}

type ProductListProps = ModelType

type QuantitiesSelectedType = {
  [key: string]: number
}

// globals
const EMPTY_IMAGE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABAQMAAAAl21bKAAAAA1BMVEUAAACnej3aAAAAAXRSTlMAQObYZgAAAApJREFUCNdjYAAAAAIAAeIhvDMAAAAASUVORK5CYII=' // (!!)

const PRODUCT_CARD_ID_PREFIX = 'product-card-'
const DEFAULT_IMAGE_SIZE = '390'
const MOBILE_IMAGE_SIZE = '600'
const IMAGE_SIZE = '740'

const ProductList: React.FC<EditableComponentPropsType<ProductListProps>> =
  withComponentRefresh<ProductListProps>(({ ...props }) => {
    const isSsr = useIsSsr()
    const userData = useUserData()
    const params = useParams()
    const pageLocale = ModelManager.modelStore.getData()?.config
      ?.locale as string

    const currentParams = params || {}
    const { push, replace } = useRouter()

    // @ts-expect-error ConfigObjects are not defined
    const caConfig = AemConfigProxy?.getConfig(AemConfigProxy?.ConfigObjects)

    const { gridListFilterConfig } = PageConfig.getConfig() as {
      gridListFilterConfig: GridListFilterConfig
    }

    const {
      cqType, // disabled
      productRootPath = '',
      config,
      general,
      attributeSelector: attributeSelectorsProp = [],
      filters,
      groupBy,
      shopConfig,
      initialData,
      ...rest
    } = { ...props.model }

    const editableProps = { ...props, model: { ...rest } }

    const firstRender = useRef(true)
    const containerRef = useRef(null)

    const productIDs = useRef<string[]>([])
    const queue = useRef<DataLayerEcommerceProduct[]>([])

    const stocksTimestamp = useRef('')
    const [stocks, setStocks] = useState<Stocks>({})

    const [cookies, setCookie] = useCookies([
      GIGYA_COOKIE_UID,
      HYBRIS_ACCESS_TOKEN,
      GIGYA_COOKIE_JWT
    ])

    // "AddToCart" button party
    const isGuestAddToCart =
      shopConfig?.guestAddToCartEnabled ||
      userData?.decoratedUserType !== UserTypes.Las

    const [data, setData] = useState(initialData)
    const [disableInteractions, setDisableInteractions] = useState(true)
    const [isButtonDisabled, setIsButtonDisabled] = useState(isSsr)
    const [variantAssetsByAddToCart, setVariantAssetsByAddToCart] = useState<
      Item['assets']
    >([])
    const [variantCodeByAddToCart, setVariantCodeByAddToCart] = useState('')
    const [quantitiesSelected, setQuantitiesSelected] =
      useState<QuantitiesSelectedType>({})

    const [historyFilters, setHistoryFilters] = useState<HistoryFilter[]>([])
    const [backupHistoryFilters, setBackupHistoryFilters] = useState<
      HistoryFilter[]
    >([])

    const [cartLocalStorageJSON, setCartLocalStorage] = useLocalStorageCart()
    const productEntry = cartLocalStorageJSON?.entry

    const [activeMiniCart, setActiveMiniCart] = useState(false)

    const [dialogErrorMessageIsOpen, setDialogErrorMessageIsOpen] =
      useState(false)

    const [scrollToContainer, setScrollToContainer] = useState(false)

    const CA_HYBRIS_URL = config?.hybrisUrl
    const CA_CLIENT_ID = config?.hybrisClientId
    const CA_CLIENT_SECRET = config?.hybrisClientSecret
    const CA_HYBRIS_BASE_SITE_ID = config?.baseSiteId
    const CA_HYBRIS_LANGUAGE = config?.language
    const ERROR_MESSAGE_FROM_CA = shopConfig?.addToCartErrorMessage

    const ATTRIBUTE_SELECTOR_MARKERS = shopConfig?.attributeSelectorMarkers

    const [errorMessageContent, setErroMessageContent] = useState({
      message: null,
      title: ERROR_MESSAGE_FROM_CA,
      actions: null
    })

    const setHistory = useCallback(
      (filterStash: FilterStashType, field: HistoryFilter) => {
        setHistoryFilters((prevState: HistoryFilter[]) => {
          const newValues = filterHistoryByStash(filterStash, prevState)
          if (newValues.length < prevState.length) {
            return newValues
          }
          return [...prevState, field]
        })
      },
      []
    )

    const setUpdatedStocks = useCallback(
      async (codes: string[]) => {
        if (
          !stocksTimestamp.current ||
          Math.abs(
            new Date().getTime() - new Date(stocksTimestamp.current).getTime()
          ) > STOCKS_CACHE_MAX_AGE
        ) {
          stocksTimestamp.current = new Date().toISOString()

          const products =
            (await StockService.getProductsStockData({
              ...config,
              articleList: codes
            })) || []

          const update = products.reduce(
            (acc, { code, stock }) => ({
              ...acc,
              [code]: stock
            }),
            {}
          )

          setStocks(update)
        }
      },
      [config]
    )

    const updateQueryParams = useCallback(
      (key: string, values: string[], isPushed: boolean = true) => {
        if (canUseDom) {
          const value = values.join(',')
          const url = new URL(window?.location?.href)
          const newParams = new URLSearchParams(
            setParams(currentParams, key, value)
          )
          url.search = newParams.toString()
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          isPushed ? push(url) : replace(url)
        }
      },
      [currentParams, push, replace]
    )
    const updateMultipleParams = useCallback(
      (newData: { [key: string]: string[] }, isPushed: boolean = true) => {
        if (canUseDom) {
          const paramsData = Object.entries(newData).reduce(
            (acc, [key, values]) => ({
              ...acc,
              [key]: values.join(',')
            }),
            {}
          )
          const url = new URL(window?.location?.href)
          const newParams = new URLSearchParams(
            setMultipleParams(currentParams, paramsData)
          )
          url.search = newParams.toString()
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          isPushed ? push(url) : replace(url)
        }
      },
      [currentParams, push, replace]
    )

    const removeQueryParams = useCallback(
      (keys: string[]) => {
        if (canUseDom) {
          const url = new URL(window?.location?.href)
          const newParams = new URLSearchParams(
            removeParams(currentParams, keys)
          )
          url.search = newParams.toString()
          push(url)
        }
      },
      [currentParams, push]
    )

    const initFindFacet = useCallback(
      (facets: TagGroup[], currTagGroupId: string) =>
        facets?.find((f) => f?.tagGroupId === currTagGroupId),
      []
    )

    const initFilterOptions = useCallback(
      (facet?: TagGroup, paramValues?: string[]) =>
        facet?.tags
          ?.filter((o) => paramValues?.includes(o?.tagId))
          ?.map((o) => o?.tagId) ?? [],
      []
    )

    const initFilterData = useCallback(
      () =>
        filters?.filterItems?.reduce((acc, { tagID: currTagGroupId }) => {
          const facet = initFindFacet(data?.facets, currTagGroupId)
          const paramValues = currentParams[currTagGroupId]?.split(',') ?? []
          const values = initFilterOptions(facet, paramValues)
          return {
            ...acc,
            ...(currTagGroupId && facet ? { [currTagGroupId]: values } : {})
          }
        }, {}) ?? {},
      [
        filters?.filterItems,
        initFindFacet,
        data?.facets,
        currentParams,
        initFilterOptions
      ]
    )

    const initSortData = useCallback(
      () =>
        gridListFilterConfig?.sortFilterOptions?.find(
          ({ filteroptionLabelValue }) =>
            currentParams[SORT_PARAM_KEY] === filteroptionLabelValue
        )?.filteroptionLabelValue ?? '',
      [gridListFilterConfig, currentParams]
    )

    const getInitFilterData = useRef(() => ({
      initFilters: {},
      initSort: ''
    }))

    const [sort, setSort] = useState<string>(
      getInitFilterData.current().initSort
    )

    const [backupFiltersStash, setBackupFiltersStash] =
      useState<FilterStashType | null>(null)
    const [filtersStash, setFiltersStash] = useState<FilterStashType>(
      getInitFilterData.current().initFilters
    )

    const enabledFilters = useMemo(
      () => getEnabledFilters(data, filters, filtersStash),
      [data, filters, filtersStash]
    )

    const filtersData = useMemo(() => {
      const dataFilters = filters?.filtersVisibility
        ? {
            title: gridListFilterConfig?.filtersTitle,
            filterList: filters?.filterItems?.reduce(
              (
                acc: FilterProps[],
                {
                  tagID: tagGroupId,
                  title,
                  description,
                  allOptionVisibility,
                  datalayerFilterCategory
                }
              ) => {
                const facet = data?.facets?.find(
                  // eslint-disable-next-line max-nested-callbacks
                  (f) => f?.tagGroupId === tagGroupId
                )
                const checkedIds = filtersStash[tagGroupId] ?? []
                const enabledIds = enabledFilters[tagGroupId] ?? []
                const tags = facet?.tags
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const onChangeFilter = (updatedFilter: any, index: number) => {
                  setScrollToContainer(true)
                  const newValues = (
                    updatedFilter as {
                      id?: string
                      text?: string
                      checked?: boolean
                    }[]
                  )
                    // eslint-disable-next-line max-nested-callbacks
                    .filter((f) => f.checked && f.id !== FilterIDs.All)
                    // eslint-disable-next-line max-nested-callbacks
                    .map((f) => f.id ?? '')
                    // eslint-disable-next-line max-nested-callbacks
                    .filter((i) => i)

                  // eslint-disable-next-line max-nested-callbacks
                  setFiltersStash((prevState: FilterStashType) => {
                    const newFilterStashState = {
                      ...prevState,
                      [tagGroupId]: newValues
                    }

                    setHistory(newFilterStashState, {
                      filterId: tagGroupId,
                      value: updatedFilter[index].id
                    })
                    return newFilterStashState
                  })

                  updateQueryParams(tagGroupId, newValues, true)

                  pushDataLayerSelectedFilter(
                    datalayerFilterCategory,
                    facet?.tagGroupId ?? '',
                    updatedFilter[index]?.text ?? ''
                  )
                }

                return facet
                  ? [
                      ...acc,
                      {
                        title,
                        description,
                        id: facet.tagGroupId,
                        name: facet.tagGroupId,
                        message: '-',
                        all: allOptionVisibility,
                        allLabel: gridListFilterConfig?.filterLabel,
                        onChange: onChangeFilter,
                        checkboxList: tags?.reduce(
                          (
                            options: NonNullable<FilterProps['checkboxList']>,
                            { tagId, displayName: text, background: color }: Tag
                          ) => {
                            const values = {
                              id: tagId,
                              text,
                              checked: checkedIds.includes(tagId),
                              disabled: !enabledIds.includes(tagId),
                              ...(color ? { color } : {})
                            }

                            return [...options, values]
                          },
                          []
                        )
                      }
                    ]
                  : acc
              },
              []
            )
          }
        : undefined
      return dataFilters
    }, [
      data?.facets,
      enabledFilters,
      filters?.filterItems,
      filters?.filtersVisibility,
      filtersStash,
      gridListFilterConfig?.filterLabel,
      gridListFilterConfig?.filtersTitle,
      setHistory,
      updateQueryParams
    ])

    const onSortChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>, value?: string) => {
        setScrollToContainer(true)

        const newValue = value ?? ''
        setSort(value ?? '')

        updateQueryParams(SORT_PARAM_KEY, [newValue], true)

        pushDataLayerSelectedFilter(
          gridListFilterConfig?.sortFilterTitle,
          e.target.name,
          newValue
        )
      },
      [gridListFilterConfig?.sortFilterTitle, updateQueryParams]
    )

    const sortData = useMemo(() => {
      const dataSort = filters?.filtersVisibility
        ? {
            title: gridListFilterConfig?.sortFilterTitle,
            name: 'sorting-filter',
            id: 'sorting-filter',
            message: '-',
            onChange: onSortChange,
            value: sort,
            radioButtons: gridListFilterConfig?.sortFilterOptions?.map(
              ({
                filteroptionLabel: label,
                filteroptionLabelValue: value
              }) => ({
                label,
                value,
                checked: value === sort
              })
            )
          }
        : undefined
      return dataSort
    }, [filters?.filtersVisibility, gridListFilterConfig, onSortChange, sort])

    const getStockInfoLabel = useCallback(
      (variant: Item) => {
        const stockLevelStatus = getStockLevelStatus(variant, stocks)

        let stockLabel = '&nbsp;' // Default value to keep the line and avoid CLS
        switch (!isSsr && stockLevelStatus) {
          case StockLevelStatus.inStock:
            stockLabel = shopConfig?.inStockLabel
            break
          case StockLevelStatus.outOfStock:
            stockLabel = shopConfig?.outOfStockLabel
            break
          case StockLevelStatus.lowStock:
            stockLabel = shopConfig?.lowStockLabel
            break
          default:
            break
        }

        return stockLabel
      },
      [isSsr, shopConfig, stocks]
    )

    const getAddToCartButtonLabel = useCallback(
      (variant: Item, isPreorder: boolean) => {
        const stockLevelStatus = getStockLevelStatus(variant, stocks)

        const isButtonInDefaultState =
          !isPreorder &&
          stockLevelStatus &&
          stockLevelStatus !== StockLevelStatus.outOfStock &&
          isGuestAddToCart

        let label = ''

        if (isButtonInDefaultState) {
          label = shopConfig?.addToCartLabel
        }

        if (isPreorder && isGuestAddToCart) {
          label = shopConfig?.preOrderCTALabel
        }

        if (
          isGuestAddToCart &&
          !isPreorder &&
          shopConfig?.notifyMe &&
          stockLevelStatus === StockLevelStatus.outOfStock
        ) {
          label = shopConfig?.notifyMeCTALabel
        }

        if (
          !isPreorder &&
          !shopConfig?.notifyMe &&
          (stockLevelStatus === StockLevelStatus.outOfStock ||
            !stockLevelStatus)
        ) {
          label = shopConfig?.outOfStockCTALabel
        }

        if (
          !isGuestAddToCart &&
          (isPreorder ||
            shopConfig?.notifyMe ||
            stockLevelStatus !== StockLevelStatus.outOfStock)
        ) {
          label = shopConfig?.loginToBuyLabel
        }

        return label
      },
      [stocks, isGuestAddToCart, shopConfig]
    )

    const onVariantLink = useCallback(
      (
        variant: Partial<Item>,
        url: string,
        { list, position }: { list: string; position: number }
      ) => {
        const dataLayerEcommerceProduct =
          createDataLayerEcommerceProduct(variant)

        pushDataLayerProductListVariantClick(
          {
            ...dataLayerEcommerceProduct,
            list,
            position
          },
          url
        )
      },
      []
    )

    const onVariantColorChange = useCallback(
      (
        variant: Partial<Item>,
        { list, position }: { list: string; position: number }
      ) => {
        const dataLayerEcommerceProduct =
          createDataLayerEcommerceProduct(variant)

        pushDataLayerProductListVariantChange({
          ...dataLayerEcommerceProduct,
          list,
          position
        })
      },
      []
    )

    const sortedItemsByPrice = useMemo(
      () => data?.items?.slice().sort(sortItemsByPrice(sort)),
      [data?.items, sort]
    )

    const productList = useMemo(
      () =>
        sortedItemsByPrice?.map(({ variants }, order) => {
          const position = order + 1
          const sortedVariantsByStock = variants
            ?.slice()
            ?.sort(sortVariantsByStock(stocks))

          return {
            srOnly: !showProduct(sortedVariantsByStock, filtersStash),
            id: `${PRODUCT_CARD_ID_PREFIX}${position}`,
            'data-position': position,
            colorSelector: getColorSelector({
              variants: sortedVariantsByStock,
              stocks,
              historyFilters,
              dotsVisibility: general?.colorDotsVisibility
            }),
            variants: sortedVariantsByStock
              .map((variant) => ({
                ...variant,
                [COLOR_PROPERTY]: variant.nuance?.code
              }))
              .map((variant, variantIndex: number, variantList) => {
                const variantUrl = general?.linkToPDP
                  ? `${productRootPath.replace('$SEO_URL_PLACEHOLDER', variant?.seo?.productsSeoUrl || '')}`
                  : undefined

                const [firstAsset] = variant.assets
                let productImageData = {}
                if (firstAsset) {
                  const src = getOptimizedImageExtension(
                    firstAsset?.assetFullUrl
                  )

                  productImageData = {
                    src: `${src}?width=${DEFAULT_IMAGE_SIZE}`,
                    renditions: true,
                    srcSetData: [
                      `${src}?width=${MOBILE_IMAGE_SIZE} 390w`,
                      `${src}?width=${IMAGE_SIZE} 600w`,
                      `${src}?width=${IMAGE_SIZE}`
                    ].toString()
                  }
                }

                const imageData = {
                  fallback: EMPTY_IMAGE,
                  alt: variant?.globalArticleName,
                  ...productImageData,
                  width: DEFAULT_IMAGE_SIZE,
                  height: DEFAULT_IMAGE_SIZE,
                  aspectRatio: true,
                  lazyLoading: position !== 1 || variantIndex !== 0,
                  ...(variantUrl && {
                    linkUrl: variantUrl,
                    onClick() {
                      onVariantLink(variant, variantUrl, {
                        list: general?.dataLayerListName,
                        position
                      })
                    }
                  })
                }

                const productChips = variant.badges?.length
                  ? {
                      chipPropsList: variant.badges
                        .filter(({ badgeDescription }) =>
                          Boolean(badgeDescription)
                        )
                        .map((badge) => ({
                          background: badge.backgroundColor?.hexCode,
                          color: badge.fontColor?.hexCode,
                          label: badge.badgeDescription
                        }))
                    }
                  : undefined

                const nameData = variant?.globalArticleName
                  ? {
                      productName: variant.globalArticleName,
                      ...(variantUrl && {
                        linkUrl: variantUrl,
                        onClick() {
                          onVariantLink(variant, variantUrl, {
                            list: general?.dataLayerListName,
                            position
                          })
                        }
                      })
                    }
                  : undefined

                const showRating =
                  general?.ratingsVisibility &&
                  shopConfig?.ratesAndReviewsEnabled
                const rating = showRating
                  ? {
                      productId: variant.globalArticleCode,
                      type: RatingAndReviewsTypes.InlineRating
                    }
                  : undefined

                const showShortDescriptionData = variant.productShortDescription
                const shortDescriptionData = showShortDescriptionData
                  ? {
                      description: variant.productShortDescription
                    }
                  : undefined

                const showStockInfo = Boolean(
                  getStockLevelStatus(variant, stocks)
                )
                const stockInfo = showStockInfo
                  ? {
                      stockLabel: getStockInfoLabel(variant),
                      stockStatus:
                        stockStatusMap[getStockLevelStatus(variant, stocks)],
                      showStockMessage:
                        stockStatusMap[getStockLevelStatus(variant, stocks)] !==
                          StockStatuses.InStock ||
                        shopConfig?.showInStockInfoEnable
                    }
                  : undefined

                const priceInfo = getPriceInfo(variant?.price, pageLocale)

                const { attributeSelectors, attributeValues } =
                  getAttributeSelectors({
                    attributeSelectorsProp,
                    stocks,
                    variantList,
                    variant,
                    attributeSelectorMarkers: ATTRIBUTE_SELECTOR_MARKERS,
                    locale: pageLocale
                  })

                const orderQTYValues = getOrderQuantityValues(
                  variant.minimumOrderQuantity,
                  variant.maximumOrderQuantity
                )

                const outOfStock =
                  stockStatusMap[getStockLevelStatus(variant, stocks)] ===
                  StockStatuses.OutOfStock

                const isPreorder = checkIsPreorder(variant?.preOrderUntilDate)

                const addToCartButtonLabel = getAddToCartButtonLabel(
                  variant,
                  isPreorder
                )

                const isQTYSelectorVisible =
                  general?.quantitySelectorVisibility &&
                  !outOfStock &&
                  !isPreorder

                const dataLayerEcommerceProduct =
                  createDataLayerEcommerceProduct(variant)

                const addToCartMethodConfig = {
                  orderTypes: general?.orderType,
                  setIsButtonDisabled,
                  setIsButtonLoading(bool: boolean) {
                    setVariantAssetsByAddToCart(variant?.assets ?? [])
                    setVariantCodeByAddToCart(
                      bool ? variant.globalArticleCode : ''
                    )
                  },
                  setErroMessageContent,
                  isLoginUser: userData?.decoratedUserType !== UserTypes.Las,
                  errorContent: {
                    exceededMaximumQuantityTitle:
                      shopConfig?.exceededMaximumQuantityTitle,
                    exceededMaximumQuantityMessage:
                      shopConfig?.exceededMaximumQuantityMessage,
                    differentOrderTypeTitle:
                      shopConfig?.differentOrderTypeTitle,
                    differentOrderTypeMessage:
                      shopConfig?.differentOrderTypeMessage,
                    differentLoyaltyRequiredTitle:
                      shopConfig?.differentLoyaltyRequiredTitle,
                    differentLoyaltyRequiredMessage:
                      shopConfig?.differentLoyaltyRequiredMessage,
                    differentOrderTypeConfirmCTALabel:
                      shopConfig?.differentOrderTypeConfirmCTALabel,
                    loginRequiredTitle: shopConfig?.loginRequiredTitle,
                    loginRequiredMessage: shopConfig?.loginRequiredMessage,
                    loginRequiredCTALabel: shopConfig?.loginRequiredCTALabel,
                    loginPageURL: shopConfig?.loginPageURL,
                    addToCartDialogCloseAriaLabel:
                      shopConfig?.addToCartDialogCloseAriaLabel,
                    addToCartDialogCancelCTALabel:
                      shopConfig?.addToCartDialogCancelCTALabel
                  },
                  productID: variant?.globalArticleCode,
                  currentProductVariant: variant,
                  productQuantity:
                    quantitiesSelected[variant.globalArticleCode] ??
                    Number(variant.minimumOrderQuantity),
                  hybrisConfig: {
                    CA_HYBRIS_URL,
                    CA_CLIENT_ID,
                    CA_CLIENT_SECRET,
                    CA_HYBRIS_BASE_SITE_ID,
                    CA_HYBRIS_LANGUAGE,
                    HYBRIS_ACCESS_TOKEN
                  },
                  defaultErrorMessage: ERROR_MESSAGE_FROM_CA,
                  mulesoftConfig: { ...caConfig?.muleSoftConfig },
                  market: caConfig?.market,
                  dataLayerEcommerceProduct,
                  setCookie,
                  cookies,
                  CORESITE_LOCALSTORAGE,
                  language: caConfig?.lang,
                  setDialogErrorMessageIsOpen,
                  cartLocalStorageJSON,
                  setCartLocalStorage,
                  setActiveMiniCart
                }

                const addToCart = {
                  buttonLabel: addToCartButtonLabel,
                  productStatus:
                    stockStatusMap[getStockLevelStatus(variant, stocks)],
                  qtySelectorProps: {
                    'aria-label': shopConfig?.quantityLabel,
                    defaultQTYValue: orderQTYValues?.[0]?.value,
                    ...(isQTYSelectorVisible &&
                      addToCartButtonLabel === shopConfig?.addToCartLabel && {
                        orderQTY: orderQTYValues
                      })
                  },
                  loadingButton:
                    variantCodeByAddToCart === variant.globalArticleCode,
                  disabledButton:
                    addToCartButtonLabel === shopConfig?.outOfStockCTALabel ||
                    isButtonDisabled,
                  onButtonClick() {
                    handleAddToCart({
                      ...addToCartMethodConfig
                    })
                  },
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onQTYChange(e: any) {
                    setQuantitiesSelected(
                      (prevValue: QuantitiesSelectedType) => ({
                        ...prevValue,
                        [variant.globalArticleCode]: Number(e.target.value)
                      })
                    )
                  }
                }

                const flavourProfiles = getFavourProfiles(
                  variant,
                  shopConfig,
                  general?.flavourProfilesVisibility
                )

                return {
                  colorValue: variant.nuance?.code,
                  attributeValues,
                  imageData,
                  productChips,
                  nameData,
                  rating,
                  shortDescriptionData,
                  stockInfo,
                  priceInfo,
                  addToCart,
                  attributeSelectors,
                  flavourProfiles,
                  code: variant.globalArticleCode
                }
              }),
            onChange(e, values, current) {
              setUpdatedStocks(data?.gcodes)

              if (values.type === ProductCardChangeEvents.Color) {
                const index = position - 1
                const [variant] = sortedItemsByPrice[index].variants.filter(
                  ({ globalArticleCode }) => current?.code === globalArticleCode
                )

                onVariantColorChange(variant, {
                  list: general?.dataLayerListName,
                  position
                })
              }
            }
          }
        }),
      [
        sortedItemsByPrice,
        stocks,
        filtersStash,
        historyFilters,
        general?.colorDotsVisibility,
        general?.linkToPDP,
        general?.ratingsVisibility,
        general?.quantitySelectorVisibility,
        general?.orderType,
        general?.flavourProfilesVisibility,
        general?.dataLayerListName,
        productRootPath,
        shopConfig,
        getStockInfoLabel,
        pageLocale,
        attributeSelectorsProp,
        ATTRIBUTE_SELECTOR_MARKERS,
        getAddToCartButtonLabel,
        userData?.decoratedUserType,
        quantitiesSelected,
        CA_HYBRIS_URL,
        CA_CLIENT_ID,
        CA_CLIENT_SECRET,
        CA_HYBRIS_BASE_SITE_ID,
        CA_HYBRIS_LANGUAGE,
        ERROR_MESSAGE_FROM_CA,
        caConfig?.muleSoftConfig,
        caConfig?.market,
        caConfig?.lang,
        setCookie,
        cookies,
        cartLocalStorageJSON,
        setCartLocalStorage,
        variantCodeByAddToCart,
        isButtonDisabled,
        onVariantLink,
        setUpdatedStocks,
        data?.gcodes,
        onVariantColorChange
      ]
    )

    const header = useMemo(
      () => ({
        title: general?.title
          ? `${general?.title} (${productList?.length || 0})`
          : '',
        description: general?.description
      }),
      [general, productList?.length]
    )

    // minicart unify !?
    const addedProductBadges = useMemo(
      () =>
        productEntry?.product?.productAvailableBadges?.productAvailableBadge
          ?.length > 0 &&
        productEntry?.product?.productAvailableBadges?.productAvailableBadge?.map(
          (
            badgeProps: unknown & {
              badge: { name: string; color: { hexCode?: string } }
            }
          ) => ({
            label: badgeProps.badge.name,
            background: badgeProps?.badge?.color?.hexCode
          })
        ),
      [productEntry?.product?.productAvailableBadges?.productAvailableBadge]
    )
    const priceInfoProps = useMemo(
      () => ({
        ...(productEntry?.totalPrice?.formattedValue ===
        productEntry?.totalWithoutDiscounts?.formattedValue
          ? {
              currentPriceTextProps: {
                content: productEntry?.totalPrice?.formattedValue
              }
            }
          : {
              currentPriceTextProps: {
                content: productEntry?.totalPrice?.formattedValue
              },
              originalPriceTextProps: {
                content: productEntry?.totalWithoutDiscounts.formattedValue
              }
            })
      }),
      [
        productEntry?.totalPrice?.formattedValue,
        productEntry?.totalWithoutDiscounts.formattedValue
      ]
    )
    const configPopoverMiniCart = useMemo(
      () => ({
        open: activeMiniCart,
        verticalPositionAnchor: PopoverVerticalPositionProps.Top,
        horizontalPositionAnchor: PopoverHorizontalPositionProps.Right,
        verticalTransform: PopoverVerticalPositionProps.Top,
        horizontalTransform: PopoverHorizontalPositionProps.Center
      }),
      [activeMiniCart]
    )

    const productImageProps = {
      height: '100',
      width: '100',
      src: `${variantAssetsByAddToCart?.[0]?.assetFullUrl}?width=100`
    }

    const configMiniCart = {
      popoverProps: configPopoverMiniCart,
      title: shopConfig?.successMessage,
      onClose: () => setActiveMiniCart(false),
      ...(addedProductBadges && {
        productChipsProps: { chipPropsList: addedProductBadges }
      }),
      priceInfoProps,
      ...(variantAssetsByAddToCart?.length
        ? {
            productImageProps
          }
        : {}),
      productName: productEntry?.product?.name,
      productVariantName: productEntry?.product?.nuance?.description,
      productQuantity: productEntry?.quantity,
      productQuantityLabel: shopConfig?.quantityLabel,
      actions: [
        {
          label: shopConfig?.checkoutCTALabel,
          linkUrl: shopConfig?.checkoutCTAUrl,
          onClick: pushDataLayerMiniCartValidateOrderClick
        },
        {
          label: `${shopConfig?.cartCTALabel} (${cartLocalStorageJSON?.cart?.totalUnitCount})`,
          linkUrl: shopConfig?.cartCTAUrl,
          onClick: pushDataLayerMiniCartSeeCartClick
        }
      ],
      idleCloseTimeout: 5000,
      blurCloseTimeout: 3000,
      'aria-label': shopConfig?.closeButtonAriaLabel
    }

    const configCartErrorMessageDialog = {
      errorMessageContent,
      dialogErrorMessageIsOpen,
      setDialogErrorMessageIsOpen
    }

    const dialogButtonsCTA = useMemo(
      () => ({
        openProps: {
          label: gridListFilterConfig?.openFiltersLabel,
          onClick() {
            setBackupFiltersStash(filtersStash)
            setBackupHistoryFilters(historyFilters)
          }
        },
        closeProps: {
          'aria-label': gridListFilterConfig?.closeFiltersButtonArialabel,
          onClick() {
            setFiltersStash(backupFiltersStash as FilterStashType)
            setHistoryFilters(backupHistoryFilters)
            updateMultipleParams(backupFiltersStash as FilterStashType)
            setBackupFiltersStash(null)
            setBackupHistoryFilters([])
          }
        },
        applyProps: {
          label: gridListFilterConfig?.applyFiltersLabel,
          onClick() {
            setBackupFiltersStash(null)
            setBackupHistoryFilters([])
          }
        },
        resetProps: {
          label: gridListFilterConfig?.removeFiltersLabel,
          onClick() {
            setSort('')
            setFiltersStash((prevState) => {
              removeQueryParams([...Object.keys(prevState), SORT_PARAM_KEY])
              return Object.keys(prevState).reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr]: []
                }),
                {}
              )
            })
            setHistoryFilters([])
          }
        }
      }),
      [
        backupFiltersStash,
        backupHistoryFilters,
        filtersStash,
        gridListFilterConfig?.applyFiltersLabel,
        gridListFilterConfig?.closeFiltersButtonArialabel,
        gridListFilterConfig?.openFiltersLabel,
        gridListFilterConfig?.removeFiltersLabel,
        historyFilters,
        removeQueryParams,
        updateMultipleParams
      ]
    )

    useEffect(() => {
      if (!initialData) {
        const fetchData = async () => {
          const res = await fetchProductsData(
            config,
            general?.superTags,
            groupBy
          )
          setData(res?.data)
        }
        fetchData()
      }
    }, [])

    useEffect(() => {
      setIsButtonDisabled(false)

      // for initial SSR render
      if (data?.gcodes?.length && firstRender.current === true) {
        firstRender.current = false
        setUpdatedStocks(data?.gcodes)
      }
    }, [data?.gcodes, setUpdatedStocks])

    useEffect(() => {
      getInitFilterData.current = () => ({
        initFilters: initFilterData(),
        initSort: initSortData()
      })
    }, [
      data?.facets,
      filters?.filterItems,
      initFilterData,
      initSortData,
      currentParams
    ])

    useEffect(() => {
      if (params) {
        setDisableInteractions(false)
      }
    }, [Boolean(params)])

    useEffect(() => {
      if (filters?.filterItems && data?.facets && params) {
        const { initFilters, initSort } = getInitFilterData.current()
        setHistoryFilters(getHistoryFromStash(initFilters))
        setFiltersStash(initFilters)
        setSort(initSort)
      }
    }, [filters?.filterItems, data?.facets, Boolean(params)])

    useEffect(() => {
      if (
        Object.entries(filtersStash).some(([key, value]) =>
          value.some((item) => !enabledFilters[key].includes(item))
        )
      ) {
        setFiltersStash((prevFiltersStash) => {
          const newFiltersStash = Object.entries(prevFiltersStash).reduce(
            (acc, [key, value]) => {
              // eslint-disable-next-line max-nested-callbacks
              const newValues = value.filter((item) =>
                enabledFilters[key].includes(item)
              )
              return {
                ...acc,
                [key]: newValues
              }
            },
            {}
          )
          updateMultipleParams(newFiltersStash, false)
          setHistoryFilters((prevHistoryState) =>
            filterHistoryByStash(newFiltersStash, prevHistoryState)
          )
          return newFiltersStash
        })
      }
    }, [enabledFilters, filtersStash, updateMultipleParams])

    useEffect(() => {
      if (canUseDom && scrollToContainer) {
        scrollToElementWithOffset(
          containerRef?.current,
          document.querySelector('header')?.offsetHeight || 0
        )

        setScrollToContainer(false)
      }
    }, [scrollToContainer])

    useEffect(() => {
      const container = containerRef.current

      if (canUseDom && sortedItemsByPrice?.length && container) {
        const products = sortedItemsByPrice
        const len = 15

        const spyPage = (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            const id = String(entry.target.getAttribute('id'))
            const position = entry.target.getAttribute('data-position')

            if (
              entry.isIntersecting &&
              position &&
              !productIDs.current.includes(id)
            ) {
              productIDs.current.push(id)

              const index = +position - 1
              const variant = products[index].variants[0]

              const dataLayerEcommerceProduct =
                createDataLayerEcommerceProduct(variant)
              queue.current.push({
                ...dataLayerEcommerceProduct,
                list: general?.dataLayerListName,
                position
              })

              if (queue.current.length >= len) {
                pushDataLayerProductListView(queue.current)
                queue.current = []
              }
            }
          })
        }

        const observer = new ProductListObserver({
          container,
          selector: `[id*="${PRODUCT_CARD_ID_PREFIX}"]`,
          onLoaded: spyPage,
          onScroll: spyPage,
          onInterval() {
            if (queue.current.length) {
              pushDataLayerProductListView(queue.current)
              queue.current = []
            }
          },
          onBeforeUnload() {
            if (queue.current.length) {
              pushDataLayerProductListView(queue.current)
            }
          }
        })

        return () => {
          productIDs.current = []
          queue.current = []

          observer.disconnect()
        }
      }

      return () => {}
    }, [general?.dataLayerListName, sortedItemsByPrice])

    return (
      <EditableComponent {...editableProps}>
        <div
          ref={containerRef}
          style={{ ...(disableInteractions && { pointerEvents: 'none' }) }}
        >
          <PGL
            header={header}
            filtersData={filtersData}
            sortData={sortData}
            productList={productList}
            dialogButtons={dialogButtonsCTA}
          />
          <MiniCart {...configMiniCart} />
          <CartErrorMessageDialog {...configCartErrorMessageDialog} />
        </div>
      </EditableComponent>
    )
  })

// @ts-expect-error Property 'getServerSideProps' does not exist
ProductList.getServerSideProps = async ({ ...props }) => {
  const { config, general, groupBy } = props
  const res = await fetchProductsData(config, general?.superTags, groupBy)
  return {
    ...props,
    initialData: { ...res?.data }
  }
}

export default ProductList
